import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { BackButton } from '../../../../shared/components/back-button/back-button';
import { InviteePromptDetail, InviteePromptHistoryItem } from '../../../../core/models/schedule-prompt.models';

@Component({
  selector: 'app-invitee-view',
  imports: [RouterLink, NgClass, BackButton],
  templateUrl: './invitee-view.html',
  styleUrl: './invitee-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteeView implements OnInit {
  private readonly route      = inject(ActivatedRoute);
  private readonly api        = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly schedulePromptId = signal<string | null>(null);
  readonly hasError         = signal(false);
  readonly detail           = signal<InviteePromptDetail | null>(null);
  readonly activeFilter     = signal<'all' | 'responded' | 'not-responded' | 'waiting'>('all');

  /** Bumped on every fetch so a superseded/stale response can be discarded. */
  private requestSeq = 0;

  ngOnInit(): void {
    /**
     * Subscribe to paramMap instead of using snapshot.
     * This ensures the component re-loads data when Angular reuses the
     * same component instance during in-app navigation between different
     * prompts (e.g. clicking a different member from the listing while
     * already on an invitee-view page).
     */
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.schedulePromptId.set(params.get('schedulePromptId'));
        this.activeFilter.set('all');
        this.loadDetail();
      });
  }

  async loadDetail(): Promise<void> {
    const id = this.schedulePromptId();
    if (!id) return;

    const seq = ++this.requestSeq;
    this.hasError.set(false);
    try {
      const resp = await this.api.request<InviteePromptDetail>(
        'GET',
        `${API_ROUTES.SP_GET_INVITEE_DETAIL}/${id}`,
        undefined,
        { showToaster: false, showLoader: true, useToken: true },
      );

      if (seq !== this.requestSeq) return; // a newer request has since started — discard this stale response

      if (resp.status && resp.data) {
        this.detail.set(resp.data);
      } else {
        this.hasError.set(true);
      }
    } catch {
      if (seq !== this.requestSeq) return;
      this.hasError.set(true);
    }
  }

  /** Returns initials from a full name, e.g. "Robert King" → "RK". */
  getInitials(name: string): string {
    if (!name) return '?';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  /** True for any status value meaning the delivery window closed with nothing submitted. */
  private isNoResponseStatus(status: string): boolean {
    const s = status?.toLowerCase();
    return s === 'notresponded' || s === 'noresponse' || s === 'noresponsereceived';
  }

  /** CSS modifier class for history item wrapper. */
  getHistoryItemClass(status: string): string {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s === 'responded') return 'fm-responded';
    if (this.isNoResponseStatus(status)) return 'fm-not-responded';
    return 'fm-waiting';
  }

  /** CSS badge class. */
  getStatusBadgeClass(status: string): string {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s === 'responded') return 'fm-status-success';
    if (this.isNoResponseStatus(status)) return 'fm-status-danger';
    return 'fm-status-warning';
  }

  /** Bootstrap icon for the timeline dot. */
  getTimelineIcon(status: string): string {
    if (!status) return 'bi-hourglass-split';
    const s = status.toLowerCase();
    if (s === 'responded') return 'bi-check-lg';
    if (this.isNoResponseStatus(status)) return 'bi-x-lg';
    return 'bi-hourglass-split';
  }

  /** Human-readable status label. */
  getStatusLabel(status: string): string {
    if (!status) return 'Awaiting Response';
    const s = status.toLowerCase();
    if (s === 'responded') return 'Responded';
    if (this.isNoResponseStatus(status)) return 'No Response Received';
    return 'Awaiting Response';
  }

  /** Filtered previous questions for the history tab. */
  readonly filteredHistory = computed<InviteePromptHistoryItem[]>(() => {
    const all = this.detail()?.promptHistory ?? [];
    switch (this.activeFilter()) {
      case 'responded':     return all.filter((q) => q.responseStatus?.toLowerCase() === 'responded');
      case 'not-responded': return all.filter((q) => this.isNoResponseStatus(q.responseStatus));
      case 'waiting':       return all.filter((q) => q.responseStatus?.toLowerCase() === 'awaitingresponse');
      default:              return all;
    }
  });

  setFilter(f: 'all' | 'responded' | 'not-responded' | 'waiting'): void {
    this.activeFilter.set(f);
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
}
